import { HashtagIcon } from "@heroicons/react/24/outline";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";

import { EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  CaretSortIcon,
  CheckIcon,
  ChevronRightIcon,
  Pencil2Icon,
} from "@radix-ui/react-icons";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetSettings } from "src/api/General/settings";
import { useGetUsers } from "src/api/General/users";
import {
  useGetSlackChannels,
  useGetSlackIntegrationStatus,
} from "src/api/Integrations/slack";
import {
  SpaceData,
  SpaceMembershipData,
  useAddUserToSpace,
  useDeleteSpace,
  useGetSpace,
  useGetSpaceMemberships,
  useRemoveUserFromSpace,
  useUpdateSpace,
  useUpdateSpaceMembership,
} from "src/api/Spaces/spaces";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import InputHeading from "src/components/ui/Headings/InputHeading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import { SpaceNameAndSymbol } from "src/pages/spaces/spaces";

type Props = {};

export default function SpaceMembersSettings({}: Props) {
  const { spaceId } = useParams();
  const { data: space } = useGetSpace(String(spaceId));
  const { data: settings } = useGetSettings();
  const [deletingOpen, setDeletingOpen] = useState(false);
  const deleteSpaceMutation = useDeleteSpace();
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  useEffect(() => {
    if (deleteSpaceMutation.isSuccess) {
      setDeletingOpen(false);
    }
  }, [deleteSpaceMutation.isSuccess]);
  const [open, setOpen] = useState(false);
  const addUserToSpaceMutation = useAddUserToSpace();
  const { data: memberships } = useGetSpaceMemberships(String(spaceId));
  const { users } = useGetUsers();
  const addOptions = useMemo(() => {
    if (!users || !memberships) return [];
    return users.filter(
      (user) =>
        !memberships?.some((m) => m.user._id.toString() === user._id.toString())
    );
  }, [memberships, users]);
  const [requireInternalProjectUpdates, setRequireInternalProjectUpdates] =
    useState(false);

  return (
    <div className="flex h-screen flex-col bg-neutral-50/50">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/settings/spaces/${spaceId}`}>
            {space && (
              <SpaceNameAndSymbol name={space.name} color={space.color} />
            )}
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Projects"}</div>
        </div>
      </div>
      <div className="mx-auto w-full max-w-2xl pt-20">
        <div className="space-y-1 border-b border-gray-200/70 pb-6">
          <div className="text-xl font-medium">Projects</div>
          {/* <div className="text-sm font-light text-gray-600">
            Edit the default settings for projects in this space
          </div> */}
        </div>
        <InternalProjectUpdates />
      </div>
    </div>
  );
}

function InternalProjectUpdates() {
  const { spaceId } = useParams();
  const { data: space } = useGetSpace(String(spaceId));
  const [
    switchingOnInternalProjectUpdates,
    setSwitchingOnInternalProjectUpdates,
  ] = useState(false);
  const [
    switchingOffInternalProjectUpdates,
    setSwitchingOffInternalProjectUpdates,
  ] = useState(false);
  const updateSpaceMutation = useUpdateSpace();

  useEffect(() => {
    if (updateSpaceMutation.isSuccess && switchingOffInternalProjectUpdates) {
      setSwitchingOffInternalProjectUpdates(false);
    }
  }, [updateSpaceMutation.isSuccess]);

  return (
    <div className="space-y-3.5 pt-8">
      <div className="space-y-0">
        <div className="font-medium text-gray-800">
          Internal Project Updates
        </div>
        <div className="text-sm font-light text-gray-500">
          Internal project updates help you understand the progress and health
          of your projects
        </div>
      </div>

      <div className="space-y-1">
        <div className="space-y-5 rounded-md border border-gray-200/70 bg-white p-8 pt-7 shadow-sm">
          <div className="flex items-center justify-between gap-10">
            <div className="space-y-0">
              <div className="text-sm font-medium text-gray-800">
                Require Updates
              </div>
              <div className="max-w-prose text-[13px] font-light text-gray-500">
                Project owners will be required to submit updates at a defined
                frequency
              </div>
            </div>
            <Switch
              checked={
                space?.settings.internalUpdateRequirement.isEnabled ||
                switchingOnInternalProjectUpdates
              }
              onCheckedChange={(value) => {
                if (!!value) {
                  setSwitchingOnInternalProjectUpdates(true);
                } else {
                  setSwitchingOffInternalProjectUpdates(true);
                }
              }}
            />
          </div>
          {space?.settings.internalUpdateRequirement.isEnabled && (
            <div className="space-y-7">
              <div className="space-y-1.5 border-t border-gray-200/50 pt-5">
                <div className="space-y-0">
                  <div className="text-sm font-medium text-gray-800">
                    Frequency
                  </div>
                  <div className="text-[13px] font-light text-gray-500">
                    The frequency at which project owners will be required to
                    submit updates
                  </div>
                </div>
                {space?.settings.internalUpdateRequirement.due && (
                  <InternalUpdateFrequency
                    due={space?.settings.internalUpdateRequirement.due}
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <div className="space-y-0">
                  <div className="text-sm font-medium text-gray-800">
                    Escalation
                  </div>
                  <div className="text-[13px] font-light text-gray-500">
                    The escalation workflow for when an internal update goes
                    overdue
                  </div>
                </div>

                <InternalUpdateEscalation
                  escalation={
                    space?.settings.internalUpdateRequirement.escalation
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <Dialog
        open={switchingOffInternalProjectUpdates}
        onOpenChange={setSwitchingOffInternalProjectUpdates}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Disable Required Updates</DialogTitle>
            {/* <DialogDescription>
              To enable required internal project updates, set their frequency
              and grace period
            </DialogDescription> */}
          </DialogHeader>
          <div className="py-2 text-sm text-gray-600">
            Are you sure you want to disable required internal project updates
            for the space{" "}
            <span className="font-medium text-gray-800">{space?.name}</span>?
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setSwitchingOffInternalProjectUpdates(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                updateSpaceMutation.mutate({
                  _id: String(spaceId),
                  settings: {
                    internalUpdateRequirement: {
                      isEnabled: false,
                    },
                  },
                });
              }}
              disabled={updateSpaceMutation.isLoading}
            >
              {updateSpaceMutation.isLoading ? "Disabling..." : "Disable"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <SwitchingOnInternalProjectUpdatesDialog
        setSwitchingOnInternalProjectUpdates={
          setSwitchingOnInternalProjectUpdates
        }
        switchingOnInternalProjectUpdates={switchingOnInternalProjectUpdates}
      />
    </div>
  );
}

function SwitchingOnInternalProjectUpdatesDialog({
  setSwitchingOnInternalProjectUpdates,
  switchingOnInternalProjectUpdates,
}: {
  setSwitchingOnInternalProjectUpdates: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  switchingOnInternalProjectUpdates: boolean;
}) {
  const { spaceId } = useParams();
  const updateSpaceMutation = useUpdateSpace();
  const [weeks, setWeeks] = useState("2");
  const { data: space } = useGetSpace(String(spaceId));
  const [escalationWorkflow, setEscalationWorkflow] = useState<
    "slack-notification" | "none"
  >("slack-notification");
  const { data: slackIntegrationStatus } = useGetSlackIntegrationStatus();
  const { data: channels } = useGetSlackChannels();
  const [channelSearch, setChannelSearch] = useState("");
  const [overdueEscalationDays, setOverdueEscalationDays] = useState("3");
  const [slackNotificationChannelOpen, setSlackNotificationChannelOpen] =
    useState(false);
  useEffect(() => {
    if (updateSpaceMutation.isSuccess && switchingOnInternalProjectUpdates) {
      setSwitchingOnInternalProjectUpdates(false);
    }
  }, [updateSpaceMutation.isSuccess]);
  const [slackChannelId, setSlackChannelId] = useState<string | null>(null);
  return (
    <Dialog
      open={switchingOnInternalProjectUpdates}
      onOpenChange={setSwitchingOnInternalProjectUpdates}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Required Updates</DialogTitle>
          {/* <DialogDescription>
          Set the frequency and escalation workflow for required updates
        </DialogDescription> */}
        </DialogHeader>
        <div className="space-y-8 py-4">
          <div className="space-y-1.5">
            <div className="space-y-0">
              <div className="text-sm font-medium text-gray-800">Frequency</div>
              <div className="text-[13px] font-light text-gray-500">
                The frequency at which project owners will be required to submit
                updates
              </div>
            </div>
            <div className="flex items-center gap-1.5 rounded-md bg-gray-100/70 px-4 py-2.5">
              <div className="text-sm text-gray-800">Every</div>
              <Select value={weeks} onValueChange={setWeeks}>
                <SelectTrigger className="h-7 w-fit bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 week</SelectItem>
                  <SelectItem value="2">2 weeks</SelectItem>
                  <SelectItem value="3">3 weeks</SelectItem>
                  <SelectItem value="4">4 weeks</SelectItem>
                </SelectContent>
              </Select>
              {/* <div className="text-sm text-gray-800">on</div>
            <Select value={newDay} onValueChange={setNewDay}>
              <SelectTrigger className="h-7 w-fit bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monday">Monday</SelectItem>
                <SelectItem value="tuesday">Tuesday</SelectItem>
                <SelectItem value="wednesday">Wednesday</SelectItem>
                <SelectItem value="thursday">Thursday</SelectItem>
                <SelectItem value="friday">Friday</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-gray-800">at</div>
            <Select value={newTime} onValueChange={setNewTime}>
              <SelectTrigger className="h-7 w-fit bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="8:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">8:00</div>{" "}
                    <div className="text-xs">AM</div>
                  </div>
                </SelectItem>
                <SelectItem value="9:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">9:00</div>{" "}
                    <div className="text-xs">AM</div>
                  </div>
                </SelectItem>
                <SelectItem value="10:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">10:00</div>{" "}
                    <div className="text-xs">AM</div>
                  </div>
                </SelectItem>
                <SelectItem value="11:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">11:00</div>{" "}
                    <div className="text-xs">AM</div>
                  </div>
                </SelectItem>
                <SelectItem value="12:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">12:00</div>{" "}
                    <div className="text-xs">PM</div>
                  </div>
                </SelectItem>
                <SelectItem value="13:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">1:00</div>{" "}
                    <div className="text-xs">PM</div>
                  </div>
                </SelectItem>
                <SelectItem value="14:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">2:00</div>{" "}
                    <div className="text-xs">PM</div>
                  </div>
                </SelectItem>
                <SelectItem value="15:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">3:00</div>{" "}
                    <div className="text-xs">PM</div>
                  </div>
                </SelectItem>
                <SelectItem value="16:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">4:00</div>{" "}
                    <div className="text-xs">PM</div>
                  </div>
                </SelectItem>
                <SelectItem value="17:00">
                  <div className="flex items-center gap-1">
                    <div className="w-9 text-right">5:00</div>{" "}
                    <div className="text-xs">PM</div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select> */}
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="space-y-0">
              <div className="text-sm font-medium text-gray-800">
                Escalation Workflow
              </div>
              <div className="text-[13px] font-light text-gray-500">
                The escalation workflow for when internal updates go overdue
              </div>
            </div>
            <div className="flex items-center gap-1.5 rounded-md bg-gray-100/70 p-4 py-3">
              <Select
                value={escalationWorkflow}
                onValueChange={(value) =>
                  setEscalationWorkflow(value as "slack-notification" | "none")
                }
              >
                <SelectTrigger className="h-7 w-fit bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="slack-notification">
                    Slack notification
                  </SelectItem>
                  <SelectItem value="none">No escalation workflow</SelectItem>
                </SelectContent>
              </Select>
              {escalationWorkflow === "slack-notification" && (
                <>
                  <div className="text-sm text-gray-800">in</div>
                  <Popover
                    open={slackNotificationChannelOpen}
                    onOpenChange={setSlackNotificationChannelOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="secondary"
                        size="lg"
                        role="combobox"
                        // aria-expanded={open}
                        className="h-7 w-fit max-w-full justify-between space-x-2 truncate"
                      >
                        <div className="flex items-center space-x-1">
                          <HashtagIcon className="h-3.5 w-3.5 shrink-0" />
                          {!slackChannelId ? (
                            <div className="font-normal text-gray-300">
                              Select channel
                            </div>
                          ) : (
                            <div className="truncate">
                              {
                                channels?.find((c) => c.id === slackChannelId)
                                  ?.name
                              }
                            </div>
                          )}
                        </div>
                        <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
                      align="start"
                    >
                      <Command
                      // value={slackChannelId ? slackChannelId : undefined}
                      // filter={(value, search) => {
                      //   return value
                      //     .toLowerCase()
                      //     .includes(search.toLowerCase())
                      //     ? 1
                      //     : 0;
                      // }}
                      >
                        <CommandInput
                          value={channelSearch}
                          onValueChange={(s) => {
                            setChannelSearch(s);
                          }}
                          placeholder="Search channel..."
                          className="h-9"
                        />
                        <CommandList>
                          <CommandEmpty>No channel found.</CommandEmpty>
                          <CommandGroup>
                            {channels?.map((channel) => (
                              <CommandItem
                                key={channel.id}
                                value={channel.name}
                                onSelect={() => {
                                  setSlackChannelId(channel.id);
                                  setSlackNotificationChannelOpen(false);
                                }}
                                className="gap-2"
                              >
                                <div className="flex items-center space-x-1">
                                  <HashtagIcon className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate">
                                    {channel.name}
                                  </span>
                                </div>
                                <CheckIcon
                                  className={clsx(
                                    "ml-auto h-4 w-4 shrink-0",
                                    slackChannelId === channel.id
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </CommandItem>
                            ))}
                            {/* {isLoading && <div>Loading..</div>} */}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <div className="text-sm text-gray-800">when</div>
                  <Select
                    value={overdueEscalationDays}
                    onValueChange={setOverdueEscalationDays}
                  >
                    <SelectTrigger className="h-7 w-fit bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="2">2 days</SelectItem>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="4">4 days</SelectItem>
                      <SelectItem value="5">5 days</SelectItem>
                      <SelectItem value="6">6 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-sm text-gray-800">overdue</div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => setSwitchingOnInternalProjectUpdates(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              updateSpaceMutation.mutate({
                _id: String(spaceId),
                settings: {
                  internalUpdateRequirement: {
                    isEnabled: true,
                    due: {
                      // day: newDay,
                      // time: newTime,
                      frequency: {
                        unit: "week",
                        value: Number(weeks),
                      },
                    },

                    escalation: {
                      isEnabled: escalationWorkflow !== "none",
                      ...(overdueEscalationDays !== "0" && slackChannelId
                        ? {
                            duration: {
                              unit: "day",
                              value: Number(overdueEscalationDays),
                            },
                            slackChannel: slackChannelId,
                          }
                        : {}),
                    },
                  },
                },
              });
            }}
            disabled={
              updateSpaceMutation.isLoading ||
              (escalationWorkflow === "slack-notification" && !slackChannelId)
            }
          >
            {updateSpaceMutation.isLoading ? "Enabling..." : "Enable"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getGracePeriodText(day: string, time: string, gracePeriod: string) {
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const dayIndex = days.indexOf(day);
  const gracePeriodDay = days[(dayIndex + Number(gracePeriod)) % days.length];
  return `${
    gracePeriodDay[0].toUpperCase() + gracePeriodDay.slice(1)
  } at ${convertTimeTo12HourFormat(time)}`;
}

export function convertTimeTo12HourFormat(time: string) {
  const [hours, minutes] = time.split(":");
  const ampm = Number(hours) < 12 ? "AM" : "PM";
  const hour = Number(hours) % 12;
  return `${hour}:${minutes} ${ampm}`;
}

function InternalUpdateFrequency({
  due,
}: {
  due: NonNullable<SpaceData["settings"]["internalUpdateRequirement"]["due"]>;
}) {
  const [weeks, setWeeks] = useState(due.frequency.value.toString());
  const weeksToDisplay = due.frequency.value === 1 ? "week" : "weeks";
  const [isEditing, setIsEditing] = useState(false);
  const { spaceId } = useParams();
  const updateSpaceMutation = useUpdateSpace();
  useEffect(() => {
    if (updateSpaceMutation.isSuccess) {
      setIsEditing(false);
    }
  }, [updateSpaceMutation.isSuccess]);
  useEffect(() => {
    setWeeks(due.frequency.value.toString());
  }, [due]);
  // const day = due.day.charAt(0).toUpperCase() + due.day.slice(1);
  // const time = convertTimeTo12HourFormat(due.time);
  return (
    <div className="flex items-center justify-between gap-1.5 rounded-md bg-gray-100/70 px-3.5 py-2.5 pr-2.5 text-[13px] text-gray-700">
      <div className="flex items-center gap-1.5">
        <div className="text-gray-800">Every</div>
        {!isEditing && (
          <div className="inline-flex h-7 items-center rounded-md border bg-white px-2">
            {due.frequency.value} {weeksToDisplay}
          </div>
        )}
        {isEditing && (
          <Select value={weeks} onValueChange={setWeeks}>
            <SelectTrigger className="h-7 w-fit bg-white px-2 text-[13px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1" className="text-[13px]">
                1 week
              </SelectItem>
              <SelectItem value="2" className="text-[13px]">
                2 weeks
              </SelectItem>
              <SelectItem value="3" className="text-[13px]">
                3 weeks
              </SelectItem>
              <SelectItem value="4" className="text-[13px]">
                4 weeks
              </SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {!isEditing ? (
        <Button
          onClick={() => setIsEditing(true)}
          variant="secondary"
          size={"sm"}
          className="h-7 w-7 p-0"
        >
          <Pencil2Icon viewBox="0 0 16 16" className="h-3.5 w-3.5" />
        </Button>
      ) : (
        <>
          <div className="flex items-center gap-1.5">
            <Button
              size={"sm"}
              variant="secondary"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
            <Button
              size={"sm"}
              disabled={
                weeks === due.frequency.value.toString() ||
                updateSpaceMutation.isLoading
              }
              onClick={() => {
                updateSpaceMutation.mutate({
                  _id: String(spaceId),
                  settings: {
                    internalUpdateRequirement: {
                      due: {
                        frequency: {
                          unit: "week",
                          value: Number(weeks),
                        },
                      },
                    },
                  },
                });
              }}
            >
              {updateSpaceMutation.isLoading ? "Saving..." : "Save"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function InternalUpdateEscalation({
  escalation,
}: {
  escalation: SpaceData["settings"]["internalUpdateRequirement"]["escalation"];
}) {
  const [isEditing, setIsEditing] = useState(false);
  const updateSpaceMutation = useUpdateSpace();
  const { data: channels } = useGetSlackChannels();
  // const channel = channels?.find(
  //   (channel) => channel.id === escalation.slackChannel
  // );
  const [slackNotificationChannelOpen, setSlackNotificationChannelOpen] =
    useState(false);
  const [slackChannelId, setSlackChannelId] = useState(escalation.slackChannel);
  const [isEnabled, setIsEnabled] = useState(escalation.isEnabled);
  const { spaceId } = useParams();
  const [channelSearch, setChannelSearch] = useState("");
  const [overdueEscalationDays, setOverdueEscalationDays] = useState(
    escalation.duration?.value.toString() ?? "3"
  );
  useEffect(() => {
    setIsEnabled(escalation.isEnabled);
    setSlackChannelId(escalation.slackChannel);
    setOverdueEscalationDays(escalation.duration?.value.toString() ?? "3");
  }, [escalation]);

  useEffect(() => {
    if (updateSpaceMutation.isSuccess) {
      setIsEditing(false);
    }
  }, [updateSpaceMutation.isSuccess]);

  // const day = due.day.charAt(0).toUpperCase() + due.day.slice(1);
  // const time = convertTimeTo12HourFormat(due.time);
  return (
    <div className="rounded-md bg-gray-100/70 px-3.5 py-2.5 pr-2.5">
      {isEditing ? (
        <div className="flex items-center justify-between gap-6 text-[13px] text-gray-700">
          <div className="flex max-w-full items-center gap-1.5 truncate">
            <Select
              value={isEnabled ? "slack-notification" : "none"}
              onValueChange={(value) =>
                setIsEnabled(value === "slack-notification")
              }
            >
              <SelectTrigger className="h-7 w-fit bg-white px-2 text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="slack-notification">
                  Slack notification
                </SelectItem>
                <SelectItem value="none">No escalation workflow</SelectItem>
              </SelectContent>
            </Select>
            {isEnabled && (
              <>
                <div className="text-gray-800">in</div>
                <Popover
                  open={slackNotificationChannelOpen}
                  onOpenChange={setSlackNotificationChannelOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="secondary"
                      size="lg"
                      role="combobox"
                      // aria-expanded={open}
                      className="h-7 w-fit max-w-full justify-between space-x-2 truncate px-2 text-[13px] font-normal"
                    >
                      <div className="flex items-center space-x-1 truncate">
                        <HashtagIcon className="h-3.5 w-3.5 shrink-0" />
                        {!slackChannelId ? (
                          <div className="font-normal text-gray-300">
                            Select channel
                          </div>
                        ) : (
                          <div className="min-w-0 truncate">
                            {
                              channels?.find((c) => c.id === slackChannelId)
                                ?.name
                            }
                          </div>
                        )}
                      </div>
                      <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
                    align="start"
                  >
                    <Command
                    // value={slackChannelId ? slackChannelId : undefined}
                    // filter={(value, search) => {
                    //   return value
                    //     .toLowerCase()
                    //     .includes(search.toLowerCase())
                    //     ? 1
                    //     : 0;
                    // }}
                    >
                      <CommandInput
                        value={channelSearch}
                        onValueChange={(s) => {
                          setChannelSearch(s);
                        }}
                        placeholder="Search channel..."
                        className="h-9"
                      />
                      <CommandList>
                        <CommandEmpty>No channel found.</CommandEmpty>
                        <CommandGroup>
                          {channels?.map((channel) => (
                            <CommandItem
                              key={channel.id}
                              value={channel.name}
                              onSelect={() => {
                                setSlackChannelId(channel.id);
                                setSlackNotificationChannelOpen(false);
                              }}
                              className="gap-2"
                            >
                              <div className="flex items-center space-x-1">
                                <HashtagIcon className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">{channel.name}</span>
                              </div>
                              <CheckIcon
                                className={clsx(
                                  "ml-auto h-4 w-4 shrink-0",
                                  slackChannelId === channel.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                          {/* {isLoading && <div>Loading..</div>} */}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <div className="text-sm text-gray-800">when</div>
                <Select
                  value={overdueEscalationDays}
                  onValueChange={setOverdueEscalationDays}
                >
                  <SelectTrigger className="h-7 w-fit bg-white px-2 text-[13px] font-normal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 day</SelectItem>
                    <SelectItem value="2">2 days</SelectItem>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="4">4 days</SelectItem>
                    <SelectItem value="5">5 days</SelectItem>
                    <SelectItem value="6">6 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-gray-800">overdue</div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex max-w-full items-center justify-between gap-6 truncate text-[13px] text-gray-700">
          {escalation.isEnabled ? (
            <div className="flex items-center gap-1.5 truncate">
              <div className="inline-flex h-7 items-center whitespace-nowrap rounded-md border bg-white px-2">
                Slack notification
              </div>
              <div className="text-gray-800">in</div>

              <div className="inline-flex h-7 items-center gap-0.5 truncate whitespace-nowrap rounded-md border bg-white px-2">
                <HashtagIcon className="h-3 w-3 shrink-0" />
                <div className="min-w-0 truncate">
                  {
                    channels?.find(
                      (channel) => channel.id === escalation.slackChannel
                    )?.name
                  }
                </div>
              </div>
              <div className="text-gray-800">when</div>
              <div className="inline-flex h-7 items-center whitespace-nowrap rounded-md border bg-white px-2">
                {escalation.duration?.value}{" "}
                {escalation.duration?.value === 1 ? "day" : "days"}
              </div>
              <div className="text-gray-800">overdue</div>
            </div>
          ) : (
            <div className="inline-flex h-7 items-center whitespace-nowrap rounded-md border bg-white px-2">
              No escalation workflow
            </div>
          )}
          <Button
            variant="secondary"
            size={"sm"}
            className="h-7 w-7 shrink-0 p-0"
            onClick={() => setIsEditing(true)}
          >
            <Pencil2Icon viewBox="0 0 16 16" className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
      {isEditing && (
        <div className="mt-2 flex justify-end gap-1.5">
          <Button
            variant="secondary"
            size={"sm"}
            onClick={() => {
              setIsEditing(false);
              setIsEnabled(escalation.isEnabled);
              setSlackChannelId(escalation.slackChannel);
              setOverdueEscalationDays(
                escalation.duration?.value.toString() ?? "3"
              );
            }}
          >
            Cancel
          </Button>
          <Button
            size={"sm"}
            disabled={
              updateSpaceMutation.isLoading ||
              (!isEnabled && !escalation.isEnabled) ||
              (isEnabled &&
                slackChannelId === escalation.slackChannel &&
                overdueEscalationDays ===
                  escalation.duration?.value.toString()) ||
              (isEnabled && !slackChannelId)
            }
            onClick={() => {
              if (isEnabled) {
                updateSpaceMutation.mutate({
                  _id: String(spaceId),
                  settings: {
                    internalUpdateRequirement: {
                      escalation: {
                        isEnabled: true,
                        slackChannel: slackChannelId,
                        duration: {
                          unit: "day",
                          value: Number(overdueEscalationDays),
                        },
                      },
                    },
                  },
                });
              } else {
                updateSpaceMutation.mutate({
                  _id: String(spaceId),
                  settings: {
                    internalUpdateRequirement: {
                      escalation: {
                        isEnabled: false,
                      },
                    },
                  },
                });
              }
            }}
          >
            {updateSpaceMutation.isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}

// function InternalUpdateGracePeriod({
//   gracePeriod,
// }: {
//   gracePeriod: SpaceData["settings"]["internalUpdateRequirement"]["gracePeriod"];
// }) {
//   const days = gracePeriod.duration.value === 1 ? "day" : "days";
//   return (
//     <div className="flex items-center justify-between gap-1.5 rounded-md bg-gray-100/70 px-4 py-2.5 text-[13px] text-gray-700">
//       <div className="rounded-md border bg-white px-2 py-1">
//         {gracePeriod.duration.value} {days}
//       </div>
//       <Button variant="secondary" size={"sm"}>
//         Edit
//       </Button>
//     </div>
//   );
// }

function UserRow({ membership }: { membership: SpaceMembershipData }) {
  const { spaceId } = useParams();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const updateMembershipMutation = useUpdateSpaceMembership();
  const removeMembershipMutation = useRemoveUserFromSpace();
  const { data: space } = useGetSpace(String(spaceId));

  useEffect(() => {
    if (removeMembershipMutation.isSuccess) {
      setIsDeleting(false);
    }
  }, [removeMembershipMutation.isSuccess]);
  return (
    <div className="flex h-10 items-center gap-2 border-b border-gray-100 last:border-0">
      <div className="flex-1 truncate text-sm font-medium leading-none">
        {membership.user.name}
      </div>
      <div className="w-60 truncate text-sm leading-none">
        {membership.user.email}
      </div>
      <div className="w-28 text-sm leading-none">
        {membership.role === "admin" ? "Admin" : "Member"}
      </div>
      <div className="flex w-10 justify-end">
        <DropdownMenu
          open={optionsOpen}
          onOpenChange={(isOpen) => {
            setOptionsOpen(isOpen);
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant={"ghost"}
              className={clsx(
                "h-6 w-6 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
              )}
            >
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-44" align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem
                // className="py-1 focus:bg-gray-50 focus:text-red-600"
                onClick={(e) => {
                  setIsEditing(true);
                  e.stopPropagation();
                }}
                // className="py-1"
              >
                <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Edit</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                // className="py-1 focus:bg-gray-50 focus:text-red-600"
                className="focus:bg-red-50 focus:text-red-600"
                onClick={(e) => {
                  setIsDeleting(true);
                  e.stopPropagation();
                }}
              >
                <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Remove</span>
              </DropdownMenuItem>
              {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <DialogHeader>
              <DialogTitle>Edit Membership</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-1.5">
                <InputHeading heading="Member" />
                <div className="text-sm font-medium text-gray-800">
                  {membership.user.name}
                </div>
              </div>
              <div className="space-y-1.5">
                <InputHeading heading="Space" />
                {space && (
                  <SpaceNameAndSymbol name={space.name} color={space.color} />
                )}
              </div>
              <div className="space-y-1">
                <InputHeading heading="Role" />
                <Select
                  value={membership.role}
                  onValueChange={(value) => {
                    if (!spaceId) return;
                    updateMembershipMutation.mutate({
                      userId: membership.user._id,
                      spaceId: spaceId,
                      role: value as "admin" | "member",
                    });
                  }}
                >
                  <SelectTrigger className="w-40">
                    {membership.role === "admin" ? "Admin" : "Member"}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Removal</DialogTitle>
            </DialogHeader>

            <div className="my-4">
              <div className="text-sm">
                <span className="font-light text-gray-600">{`Are you sure you want to remove `}</span>
                <span className="font-medium">{`${membership.user.name}`}</span>
                <span className="font-light text-gray-600">{` from this space?`}</span>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                onClick={() => {
                  setIsDeleting(false);
                }}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                disabled={removeMembershipMutation.isLoading}
                onClick={() => {
                  removeMembershipMutation.mutate({
                    spaceId: String(spaceId),
                    userId: String(membership.user._id),
                  });
                }}
                variant={"destructive"}
              >
                {removeMembershipMutation.isLoading ? "Removing.." : "Remove"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
